<template>
  <button
    @click="toggle"
    :class="{
      '--isDark': themeDark
    }"
    class="themeToggle w-12 h-12 bg-transparent border-transparent rounded"
    title="Toggle Theme"
    aria-label="Toggle Theme"
  >
    <svg-theme />
  </button>
</template>

<script>
import SvgTheme from "./../../assets/theme.svg";

/**
 * @name Dark Mode Theme Toggler
 * @description On inital load this will determine the theme to use based on previous user preference or OS settings. Also allows the user to change the theme. When theme is manually changed, the user's preference is saved for future visits.
 * @vue-computed {String|Boolean} savedTheme - checks local storage for user's theme preference.
 * @example <theme-toggle />
 */
export default {
  components: {
    SvgTheme
  },
  data: () => ({
    themeDark: false,
    cssVars: [
      {
        title: "primary",
        light: "#9d80ae",
        dark: "#f6dc73"
      },
      {
        title: "secondary",
        light: "#f6dc73",
        dark: "#f6dc73"
      },
      {
        title: "primary-swap",
        light: "#f6dc73",
        dark: "#9d80ae"
      },
      {
        title: "text",
        light: "#1d1f21",
        dark: "#ffffff"
      },
      {
        title: "heading",
        light: "#1d1f21",
        dark: "#f6dc73"
      },
      {
        title: "app-background",
        light: "#ffffff",
        dark: "#1d1f21"
      },
      {
        title: "html-background",
        light: "#f6dc73",
        dark: "#1d1f21"
      },
      {
        title: "btn-text",
        light: "#ffffff",
        dark: "#1d1f21"
      }
    ]
  }),
  computed: {
    savedTheme() {
      return localStorage.getItem("preferredTheme")
        ? localStorage.getItem("preferredTheme")
        : false;
    }
  },
  methods: {
    determineTheme() {
      // check saved preferrence first as this is preferred over
      // the dark mode set via OS
      if (this.savedTheme === "dark") {
        return (this.themeDark = true);
      } else if (this.savedTheme === "light") {
        return (this.themeDark = false);
      }

      const osDarkMode = window.matchMedia("(prefers-color-scheme: dark)")
        .matches;
      const osLightMode = window.matchMedia("(prefers-color-scheme: light)")
        .matches;
      const isNotSpecified = window.matchMedia(
        "(prefers-color-scheme: no-preference)"
      ).matches;
      const hasNoSupport = !osDarkMode && !osLightMode && !isNotSpecified;

      if (osDarkMode) {
        return (this.themeDark = true);
      }

      if (osLightMode || hasNoSupport) {
        return (this.themeDark = false);
      }
    },
    setCssVars() {
      this.cssVars.forEach(x => {
        document.documentElement.style.setProperty(
          `--color-${x.title}`,
          this.themeDark ? x.dark : x.light
        );
      });
    },
    toggle() {
      this.themeDark = !this.themeDark;
      localStorage.setItem("preferredTheme", this.themeDark ? "dark" : "light");
      this.setCssVars();
    }
  },
  created() {
    this.determineTheme();
    this.setCssVars();
  }
};
</script>

<style lang="scss">
:root {
  --color-primary: #9d80ae;
  --color-secondary: #f6dc73;
  --color-text: #1d1f21;
  --color-heading: #1d1f21;
  --color-app-background: #ffffff;
  --color-html-background: #f6dc73;
}

.themeToggle {
  @apply .text-primary .no-underline .fill-current;

  transition: 0.2s;
}

.themeToggle,
.themeToggle:hover,
.themeToggle:focus {
  border-bottom: 0;

  &::after {
    @apply .bg-transparent;

    .--isDark & {
      @apply .bg-transparent;
    }
  }
}

.theme_svg__toggle {
  @apply .overflow-hidden .rounded-full;
}

.theme_svg__circleMtns {
  @apply .rounded-full;
}

.theme_svg__bg {
  @apply .fill-transparent;
}

.theme_svg__sun {
  // sun is up
  @apply .opacity-100;

  transform: translateY(0);
  transition: fill 0.2s, opacity 1.5s, transform 1.5s;

  // sun is down
  .--isDark & {
    @apply .opacity-0;

    transform: translateY(152%);
  }
}

.theme_svg__moon {
  // moon is down
  @apply .opacity-0;

  transform: translateY(152%);
  transition: fill 0.2s, opacity 1.5s, transform 1.5s;

  // moon is up
  .--isDark & {
    @apply .opacity-100;

    transform: translateY(0);
  }
}
</style>
